(() => {
  const header = document.querySelector("[data-header-v2]");

  if (!header || header.dataset.headerV2Ready === "true") return;

  header.dataset.headerV2Ready = "true";

  const root = document.documentElement;
  const body = document.body;

  let rafId = null;
  let lastHeight = 0;
  let lastScrolled = null;

  root.classList.add("header-v2-active");
  body.classList.add("header-v2-active");

  const px = (value) => `${Math.max(0, Math.ceil(value))}px`;

  /* Header is position: fixed (sticky fails because shopify-section wrapper
     has no height beyond the header itself). Inject a placeholder div with
     matching height in normal flow so the rest of the page doesn't jump
     up under the fixed header. */
  let placeholder = header.nextElementSibling;
  if (!placeholder || !placeholder.classList || !placeholder.classList.contains("laley-hv2-placeholder")) {
    placeholder = document.createElement("div");
    placeholder.className = "laley-hv2-placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    header.parentNode.insertBefore(placeholder, header.nextSibling);
  }

  const getHeaderHeight = () => {
    const rect = header.getBoundingClientRect();
    return Math.ceil(rect.height || header.offsetHeight || 0);
  };

  const writeHeaderVars = () => {
    const height = getHeaderHeight();

    if (!height || height === lastHeight) return;

    lastHeight = height;

    placeholder.style.height = px(height);

    root.style.setProperty("--HEADER-HEIGHT", px(height));
    root.style.setProperty("--HEADER-HEIGHT-MEDIUM", px(height));
    root.style.setProperty("--HEADER-HEIGHT-MOBILE", px(height));
    root.style.setProperty("--header-height", px(height));

    header.dataset.headerV2Height = String(height);
  };

  const NOTICE_COMPACT_SCROLL_Y = 96;

  const setScrollState = () => {
    const y = window.scrollY || 0;
    /* Hysteresis: enter scrolled state at 12px, exit at 4px.
       Avoids flicker when scrolling near the threshold. */
    const enter = 12;
    const exit = 4;
    const isScrolled = lastScrolled === true ? y > exit : y > enter;
    /* Notice bar stays visible on small scrolls so the trust message
       isn't lost. Only collapses after a real shopping scroll (~96px). */
    const noticeCompact = y > NOTICE_COMPACT_SCROLL_Y;

    header.classList.toggle("laley-hv2--notice-compact", noticeCompact);

    if (isScrolled === lastScrolled) return;
    lastScrolled = isScrolled;

    header.classList.toggle("laley-hv2--scrolled", isScrolled);
    header.classList.toggle("is-scrolled", isScrolled);
  };

  const scheduleMeasure = () => {
    if (rafId) return;

    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      setScrollState();
      writeHeaderVars();
    });
  };

  const forceMeasure = () => {
    setScrollState();
    writeHeaderVars();
  };

  if ("ResizeObserver" in window) {
    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(header);
  }

  /* Re-measure after CSS transitions (logo shrink, header compact) finish
     so --HEADER-HEIGHT settles to the final post-transition value and the
     collection sticky rail doesn't visually jump. */
  header.addEventListener("transitionend", scheduleMeasure, { passive: true });

  /* Re-measure once header images finish loading (logo can be SVG/PNG, may
     change height as it decodes). */
  const images = header.querySelectorAll("img");
  images.forEach((img) => {
    if (img.complete) return;
    img.addEventListener("load", scheduleMeasure, { once: true });
    img.addEventListener("error", scheduleMeasure, { once: true });
  });

  /* Custom webfonts (GT Flexa, Caslon) can shift the nav row height after
     swap. Re-measure when document fonts settle. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(scheduleMeasure).catch(() => {});
  }

  /* Search popdown <details> toggle expands/collapses the header — re-measure
     so collection rail snaps to the new offset. */
  const searchDetails = header.querySelectorAll("header-search-popdown details");
  searchDetails.forEach((details) => {
    details.addEventListener("toggle", () => {
      window.setTimeout(scheduleMeasure, 30);
      window.setTimeout(scheduleMeasure, 220);
    });
  });

  /* Bridge mobile search button -> the dedicated mobile search popdown
     (rendered outside the V2 header). Falls back to the right-side desktop
     popdown if the mobile-specific one isn't present.

     Previous bug: bridged to the desktop right-side search (which is
     display:none on mobile). Clicking a summary inside a hidden <details>
     toggles state but doesn't render visually. */
  const mobileSearchBtn = header.querySelector(".laley-hv2__mobile-search");
  if (mobileSearchBtn) {
    mobileSearchBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      const mobilePopdown =
        document.querySelector('header-search-popdown[data-search-popdown="mobile"]') ||
        document.querySelector('header-search-popdown:not([data-search-popdown="mobile"])');
      if (!mobilePopdown) return;

      const details = mobilePopdown.querySelector("details");
      if (!details) return;

      /* Open the <details> directly — clicking a visually-hidden summary
         is unreliable across browsers. Setting `open` toggles the panel. */
      const willOpen = !details.open;
      details.open = willOpen;
      /* Fire the toggle event so theme's popdown init picks it up. */
      details.dispatchEvent(new Event("toggle", { bubbles: false }));

      if (willOpen) {
        /* Focus the search input so the user can start typing immediately. */
        window.setTimeout(() => {
          const input = details.querySelector('input[type="search"], input[name="q"], input');
          if (input) try { input.focus(); } catch (e) {}
        }, 60);
      }
    });
  }

  /* Theme's <header-component> normally binds [data-drawer-toggle] and
     [data-cart-toggle] click handlers in its connectedCallback. V2 doesn't
     use <header-component>, so we replicate those bindings here.

     Delegated approach: listen on document for clicks bubbling from V2
     buttons. More robust than per-button binding — no risk of missing a
     button that re-renders, no need for idempotency flags. */
  if (!window.__hv2DelegationBound) {
    window.__hv2DelegationBound = true;

    document.addEventListener("click", (e) => {
      const v2Header = e.target.closest("[data-header-v2]");
      if (!v2Header) return;

      /* Drawer toggle (hamburger) */
      const drawerBtn = e.target.closest("[data-drawer-toggle]");
      if (drawerBtn) {
        const key = drawerBtn.getAttribute("data-drawer-toggle");
        const drawer = key ? document.querySelector(`[data-drawer="${key}"]`) : null;
        if (drawer) {
          e.preventDefault();
          drawer.dispatchEvent(new CustomEvent("theme:drawer:toggle", {
            bubbles: false,
            detail: { button: drawerBtn }
          }));
          /* Fallback: if the custom element listener isn't wired (timing,
             constructor error, etc), force open/close via class toggle on
             next tick. The drawer's own underlay/escape closers still work. */
          window.setTimeout(() => {
            if (!drawer.classList.contains("is-open")) {
              drawer.classList.add("is-open", "is-focused");
              document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true }));
            }
          }, 60);
        }
        return;
      }

      /* Cart toggle */
      const cartBtn = e.target.closest("[data-cart-toggle]");
      if (cartBtn) {
        const cartType = window.theme && window.theme.settings && window.theme.settings.cartType;
        if (cartType !== "drawer") return;
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          e.preventDefault();
          cartDrawer.dispatchEvent(new CustomEvent("theme:cart-drawer:show"));
          if (window.a11y) window.a11y.lastElement = cartBtn;
        }
      }
    });
  }

  window.addEventListener("scroll", scheduleMeasure, { passive: true });
  window.addEventListener("resize", scheduleMeasure, { passive: true });
  window.addEventListener("orientationchange", () => {
    window.setTimeout(scheduleMeasure, 120);
    window.setTimeout(scheduleMeasure, 380);
  }, { passive: true });

  window.addEventListener("pageshow", scheduleMeasure, { passive: true });
  document.addEventListener("shopify:section:load", scheduleMeasure);
  document.addEventListener("shopify:section:select", scheduleMeasure);
  document.addEventListener("shopify:section:deselect", scheduleMeasure);

  forceMeasure();
  window.setTimeout(forceMeasure, 60);
  window.setTimeout(forceMeasure, 300);
  window.setTimeout(forceMeasure, 900);
})();
